/**
 * Reseller console shell (TMMRSL-0028; mechanics from TMMRSL-0005): primary app bar, the
 * `LAYOUT.drawerWidth` nav with the **Reseller** section, account menu. The app bar carries the
 * reseller's brand logo (when the brand has one) and name, the Staging chip, the tenant switcher
 * (inside a tenant workspace, TMMRSL-0029), theme toggle, help and the account menu.
 * `ResellerProvider` loads the reseller and the live role before the chrome paints. Keeps 0003
 * mechanics (permanent + temporary drawer, ToastProvider, StaleBuildNotice, ThemeModeToggle,
 * HelpMenu, sign-out). Colors via `resolveTokens(useTheme())` only.
 *
 * Keyboard and landmarks (TMMRSL-0043): a skip link is the first focusable element; the nav is a
 * named `nav` landmark whose current item carries a prefix bar and weight, not colour alone
 * (NAV-001); a page navigation moves focus to `main` (A11Y-010), while a selection within a
 * master/detail page (`pageKey`) does not; the account menu opens from one labelled trigger.
 */
import { useEffect, useRef, useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme, type Theme } from '@mui/material/styles';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrentOperator, useIsOwner } from '@/auth/authHooks';
import { env } from '@/config/env';
import { ResellerProvider } from '@/reseller/ResellerProvider';
import { useReseller } from '@/reseller/resellerContext';
import { Icon, LAYOUT, initialsOf, resolveTokens } from '@/theme';
import { AccountMenu } from './AccountMenu';
import { HelpMenu } from './HelpMenu';
import { activeLeaf, visibleSections } from './navModel';
import { pageKey } from './pageKey';
import { SkipLink } from './SkipLink';
import { StaleBuildNotice } from './StaleBuildNotice';
import { ThemeModeToggle } from './ThemeModeToggle';
import { ToastProvider } from './ToastProvider';
import { visuallyHidden } from './visuallyHidden';

function AppChrome() {
  const { t } = useTranslation();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const mdUp = useMediaQuery((th: Theme) => th.breakpoints.up('md'));
  const location = useLocation();
  const operator = useCurrentOperator();
  const isOwner = useIsOwner();
  const reseller = useReseller();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountEl, setAccountEl] = useState<HTMLElement | null>(null);
  const mainRef = useRef<HTMLElement>(null);
  const active = activeLeaf(location.pathname);
  const page = pageKey(location.pathname);
  const lastPage = useRef(page);
  const displayName = operator?.name || operator?.email || '';
  const resellerName = reseller.resellerName || reseller.brand.name;

  // A page navigation moves focus to the page's `h1` (`main` when the page has none yet) and
  // scrolls to the top (A11Y-010, TMM-B7); the first render and a selection within a master/detail
  // page leave focus where it is. Comparing with the last page (not a "first run" flag) keeps
  // StrictMode's repeated mount effect from moving focus.
  useEffect(() => {
    if (lastPage.current === page) return;
    lastPage.current = page;
    titleChanges.current = 0;
    const main = mainRef.current;
    const heading = main?.querySelector<HTMLElement>('h1') ?? null;
    // With an h1 the focus move announces its title; without one the title is still to come.
    titleThreshold.current = heading ? 2 : 1;
    (heading ?? main)?.focus({ preventScroll: true });
    window.scrollTo(0, 0);
  }, [page]);

  // A late page title (loaded data names the page after it first rendered) is announced once, in a
  // polite status region (TMM-B7). When the focus move above landed on an h1 it already announced
  // the first title, so only a later, different title is announced; when the page had no h1 yet
  // (or on the first load) the first title is announced. `document.title` is set by an effect keyed
  // on the title text, so a re-render never repeats it.
  const [lateTitle, setLateTitle] = useState('');
  const titleChanges = useRef(0);
  const titleThreshold = useRef(1);
  useEffect(() => {
    const titleElement = document.querySelector('title');
    if (!titleElement) return undefined;
    let last = document.title;
    const observer = new MutationObserver(() => {
      if (document.title === last) return;
      last = document.title;
      titleChanges.current += 1;
      if (titleChanges.current >= titleThreshold.current)
        setLateTitle(document.title.split(' · ')[0] ?? '');
    });
    observer.observe(titleElement, { childList: true, characterData: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const drawer = (
    <Box
      component="nav"
      aria-label={t('shell.navLabel')}
      sx={{ width: LAYOUT.drawerWidth, pt: 1, flex: 1, minHeight: 0, overflow: 'auto' }}
    >
      {visibleSections(isOwner).map((section) => (
        <List
          key={section.id}
          subheader={
            // An <li>, the default: a list holds only list items (axe `list`, TMMRSL-0073).
            <ListSubheader disableSticky>
              {t(section.labelKey)}
            </ListSubheader>
          }
        >
          {section.items.map((leaf) => {
            const selected = active?.path === leaf.path;
            return (
              // Each link in its own list item: a list holds only <li> (axe `list`, TMMRSL-0073).
              <ListItem key={leaf.path} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={leaf.path}
                  selected={selected}
                  aria-current={selected ? 'page' : undefined}
                  onClick={() => setMobileOpen(false)}
                  sx={
                    selected
                      ? {
                          position: 'relative',
                          bgcolor: tokens.brandSoft,
                          color: tokens.link,
                          '&.Mui-selected': { bgcolor: tokens.brandSoft },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            insetInlineStart: 0,
                            top: theme.spacing(0.75),
                            bottom: theme.spacing(0.75),
                            width: 3,
                            borderRadius: 2,
                            bgcolor: tokens.brand,
                          },
                        }
                      : undefined
                  }
                >
                  <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                    <Icon name={leaf.icon} sx={{ fontSize: theme.typography.pxToRem(20) }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(leaf.labelKey)}
                    slotProps={{
                      primary: {
                        variant: 'body2',
                        sx: {
                          fontWeight: selected ? 600 : 400,
                          color: selected ? tokens.link : 'text.primary',
                        },
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      ))}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <SkipLink />
      <Drawer
        variant={mdUp ? 'permanent' : 'temporary'}
        open={mdUp || mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: LAYOUT.drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: LAYOUT.drawerWidth,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {drawer}
      </Drawer>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar position="sticky">
          <Toolbar>
            {!mdUp && (
              <Tooltip title={t('shell.openNavigation')}>
                <IconButton
                  aria-label={t('shell.openNavigation')}
                  edge="start"
                  onClick={() => setMobileOpen(true)}
                  sx={{ marginInlineEnd: 1 }}
                >
                  <Icon name="menu" />
                </IconButton>
              </Tooltip>
            )}
            {reseller.brand.logoUrl ? (
              <Box
                component="img"
                src={reseller.brand.logoUrl}
                alt={t('shell.brandLogo', { name: reseller.brand.name })}
                sx={{ height: 28, maxWidth: 120, objectFit: 'contain', marginInlineEnd: 1.5 }}
              />
            ) : null}
            <Typography component="p" variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
              {resellerName}
            </Typography>
            {mdUp ? (
              <Typography
                variant="body2"
                sx={{ marginInlineStart: 1.5, color: 'text.secondary' }}
                noWrap
              >
                {t('app.consoleName')}
              </Typography>
            ) : null}
            {env.deployEnv === 'staging' ? (
              <Chip
                label={t('deployEnv.staging')}
                size="small"
                sx={{
                  marginInlineStart: 1,
                  bgcolor: tokens.warningFill,
                  color: tokens.warningInk,
                  fontWeight: 600,
                }}
              />
            ) : null}
            <Box sx={{ flex: 1 }} />
            <ThemeModeToggle />
            <HelpMenu />
            <Tooltip title={t('shell.accountMenu')}>
              <Button
                color="inherit"
                onClick={(e) => setAccountEl(e.currentTarget)}
                aria-label={
                  displayName
                    ? t('shell.accountMenuFor', { name: displayName })
                    : t('shell.accountMenu')
                }
                aria-haspopup="menu"
                aria-expanded={accountEl ? 'true' : undefined}
                aria-controls={accountEl ? 'account-menu' : undefined}
                endIcon={<Icon name="expand_more" />}
                sx={{
                  marginInlineStart: 1,
                  gap: 1,
                  minWidth: 0,
                  textAlign: 'start',
                  fontWeight: 400,
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: tokens.brandSoft,
                    color: tokens.link,
                    fontSize: theme.typography.pxToRem(14),
                  }}
                >
                  {initialsOf(displayName)}
                </Avatar>
                {mdUp ? (
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {displayName}
                    </Typography>
                    {operator ? (
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{ display: 'block', color: 'text.secondary' }}
                      >
                        {t(`roles.${operator.role}`)}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
              </Button>
            </Tooltip>
            <AccountMenu anchorEl={accountEl} onClose={() => setAccountEl(null)} />
          </Toolbar>
        </AppBar>
        <Box
          component="main"
          id="main"
          tabIndex={-1}
          ref={mainRef}
          sx={{
            p: { xs: 2, md: 3 },
            maxWidth: LAYOUT.contentMaxWidth,
            width: '100%',
            mx: 'auto',
            '&:focus:not(:focus-visible)': { outline: 'none' },
          }}
        >
          <Outlet />
        </Box>
        <Box role="status" aria-live="polite" sx={visuallyHidden}>
          {lateTitle}
        </Box>
      </Box>
    </Box>
  );
}

export function AppLayout() {
  return (
    <ToastProvider>
      <StaleBuildNotice />
      <ResellerProvider>
        <AppChrome />
      </ResellerProvider>
    </ToastProvider>
  );
}
